import axios from 'axios';
import { GMBAccount, GMBLocation } from '../types/gmb.types';
import { debug } from "../../../../../utils/debugLogger";

/**
 * Fetch all GMB accounts accessible with the provided token
 */
export const fetchGMBAccounts = async (accessToken: string): Promise<GMBAccount[]> => {
  const apiUrl = 'https://mybusinessaccountmanagement.googleapis.com/v1/accounts';
  
  try {
    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    
    // Process the accounts to extract needed information
    return (response.data.accounts || []).map((account: any) => {
      // Extract accountId from name (e.g. "accounts/12345")
      const accountId = account.name.split('/').pop();
      
      return {
        name: account.name,
        accountId,
        accountName: account.accountName || `Account ${accountId}`,
      };
    });
  } catch (error) {
    debug.error('Error fetching GMB accounts:', error);
    throw error;
  }
};

/**
 * Fetch all locations for a specific account
 */
export const fetchLocationsByAccount = async (
  accessToken: string, 
  accountName: string
): Promise<GMBLocation[]> => {
  // Use a more complete readMask to get verification status and more details
  const apiUrl = `https://mybusinessbusinessinformation.googleapis.com/v1/${accountName}/locations?readMask=name,title,storefrontAddress,metadata`;
  const accountId = accountName.split('/').pop();
  try {
    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    
    // Process the locations to extract needed information
    return (response.data.locations || []).map((location: any) => {
      // Extract locationId from name (e.g. "accounts/12345/locations/67890")
      debug.log('Location name:', location.name); // <-- Add this
      const parts = location.name.split('/');
      const locationId = parts[parts.length - 1];
      
      // Format the address
      const address = location.storefrontAddress ? 
        [
          location.storefrontAddress.addressLines?.join(', '),
          location.storefrontAddress.locality,
          location.storefrontAddress.administrativeArea,
          location.storefrontAddress.postalCode
        ].filter(Boolean).join(', ') : 
        'No address available';
        
      return {
        name: location.name,
        title: location.title || 'Unnamed Location',
        address,
        locationId,
        accountId,
        isVerified: location.metadata?.verified || false,
      };
    });
  } catch (error) {
    debug.error('Error fetching locations:', error);
    throw error;
  }
};

/**
 * Check which locations are already connected to customers
 */
export const checkConnectedLocations = async (
  locations: GMBLocation[]
): Promise<GMBLocation[]> => {
  // This would call your backend to check which locations are already connected
  // For now, we'll just return the locations as-is
  // You would need to implement the actual API call based on your backend
  
  // Placeholder for the API call
  // const response = await axios.post('/api/check-connected-gmb-locations', { locations });
  // return response.data;
  
  return locations;
};